import React, { useState, useEffect } from 'react';
import type { SortingState } from '@tanstack/react-table';

import Heading from 'components/partials/heading/heading';
import Search from 'components/partials/search/search';
import TableComponent from 'components/partials/table-component/table-component';
import Loader from 'components/partials/loader/loader';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import api from 'api';

import { getFilesTableSettings } from 'containers/user/files/files-table-settings';
import type { FilesTableRow } from 'containers/user/files/files-table-settings';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useFilesList } from 'hooks/api/use-files';
import { useImpersonation } from 'hooks/use-impersonation';

import Icons from 'assets/icons';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

interface CurrentUserWithExtras {
  filesCount?: number;
}

interface ImpersonationWithExtras {
  impersonator?: unknown;
}

const Files = (): React.JSX.Element => {
  const [search, setSearch] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'asset.createdDate', desc: false },
  ]);

  const currentUser = useCurrentUser() as ReturnType<typeof useCurrentUser> &
    CurrentUserWithExtras;
  const impersonation = useImpersonation() as ReturnType<
    typeof useImpersonation
  > &
    ImpersonationWithExtras;

  const columns = getFilesTableSettings(currentUser, setIsDownloading);

  const handlePagination = (nextPageIndex: number): void => {
    setPageIndex(nextPageIndex);
  };

  const {
    data: filesResponse,
    isFetching,
    isError,
    isSuccess,
  } = useFilesList({
    pageSize: 10,
    pageIndex,
    sortBy: [{ id: 'viewed', desc: false }, ...sortingParams],
    filters: {
      search: { value: search },
    },
  });

  const data = (filesResponse?.data ?? []) as FilesTableRow[];
  const totalRecords = filesResponse?.pagination?.totalRecords ?? 0;
  const pageSize = filesResponse?.pagination?.pageSize ?? 10;

  useEffect(
    () => () => {
      if (!impersonation?.impersonator && currentUser.filesCount) {
        api.notifyFilesAreViewed(
          (currentUser.data as { oktaUserId: string }).oktaUserId
        );
        currentUser.setNewFilesCount();
      }
    },
    []
  );

  return (
    <ResponsiveWrapper customClass="flex flex-col mt-8 relative flex-1">
      <Heading customClass="mb-6" variant="h1" data-testid="my-files-heading">
        My Files
      </Heading>

      <Search
        {...{
          search,
          setSearch,
          customClass: 'mb-5',
          customClassIcon: 'left-[1rem]',
          ariaLabel: 'Search files',
        }}
      />

      {!isError && !!data.length ? (
        <TableComponent
          columns={columns}
          data={data}
          isLoading={isFetching}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          sortingParams={sortingParams}
          manualSorting={true}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
        />
      ) : isFetching && !isSuccess ? (
        <div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
          <Loader {...{ loading: isFetching, fullScreen: false }} />
        </div>
      ) : search ? (
        <div role="status" aria-live="polite" className="flex flex-1 flex-col">
          <EmptySpaceContent
            customClass="flex-1"
            image={
              <Icons.EmptyImageAll
                customClass="size-20"
                aria-hidden="true"
              />
            }
            text="Sorry, no results found. Please try a different keyword."
          />
        </div>
      ) : (
        <div role="status" aria-live="polite" className="flex flex-1 flex-col">
          <EmptySpaceContent
            customClass="flex-1"
            image={
              <Icons.EmptyImage
                customClass="size-20"
                aria-hidden="true"
              />
            }
            text="No files have been assigned yet"
          />
        </div>
      )}
      {isDownloading && <Loader {...{ loading: isDownloading }} />}
    </ResponsiveWrapper>
  );
};

export default Files;
